import React, { useState, useMemo, useEffect } from "react";
import { format, parseISO, subDays, startOfDay, isAfter, isBefore, isEqual } from "date-fns";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  ChevronDown, 
  ChevronUp, 
  ChevronLeft,
  ChevronRight,
  Search, 
  Users, 
  Filter,
  AlertTriangle,
  ExternalLink,
  X,
  CalendarIcon
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Registration } from "@/types/registration";

interface RegistrationsTableProps {
  registrations: Registration[];
  onViewDetails: (registration: Registration) => void;
}

type SortField = "name" | "email" | "attendee_type" | "created_at" | "ticket_count";
type SortDirection = "asc" | "desc";
type DatePreset = "all" | "today" | "7days" | "30days" | "custom";
type PageSize = 10 | 25 | 50 | 100;

const RegistrationsTable: React.FC<RegistrationsTableProps> = ({
  registrations,
  onViewDetails,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [attendeeTypeFilter, setAttendeeTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [customDateFrom, setCustomDateFrom] = useState<Date | undefined>();
  const [customDateTo, setCustomDateTo] = useState<Date | undefined>();
  const [pageSize, setPageSize] = useState<PageSize>(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Get unique attendee types for filter dropdown
  const attendeeTypes = useMemo(() => {
    const types = new Set<string>();
    registrations.forEach(r => {
      if (r.attendee_type) types.add(r.attendee_type);
    });
    return Array.from(types).sort();
  }, [registrations]);

  // Date range based on preset
  const dateRange = useMemo(() => {
    const now = new Date();
    switch (datePreset) {
      case "today":
        return { from: startOfDay(now), to: now };
      case "7days":
        return { from: startOfDay(subDays(now, 7)), to: now };
      case "30days":
        return { from: startOfDay(subDays(now, 30)), to: now };
      case "custom":
        return { from: customDateFrom, to: customDateTo };
      default:
        return { from: undefined, to: undefined };
    }
  }, [datePreset, customDateFrom, customDateTo]);

  // Filter and sort registrations
  const filteredRegistrations = useMemo(() => {
    let result = [...registrations];

    // Search filter (name or email)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r => 
        (r.first_name?.toLowerCase() || "").includes(query) ||
        (r.last_name?.toLowerCase() || "").includes(query) ||
        (r.email?.toLowerCase() || "").includes(query)
      );
    }

    // Attendee type filter
    if (attendeeTypeFilter !== "all") {
      result = result.filter(r => r.attendee_type === attendeeTypeFilter);
    }

    // Status filter
    if (statusFilter === "flagged") {
      result = result.filter(r => r.has_ticket_conflict && !r.reviewed_at);
    } else if (statusFilter === "reviewed") {
      result = result.filter(r => r.reviewed_at);
    } else if (statusFilter === "eventbrite") {
      result = result.filter(r => r.registration_source === "eventbrite" || r.eventbrite_status);
    }

    // Date range filter
    if (dateRange.from || dateRange.to) {
      result = result.filter(r => {
        const regDate = parseISO(r.created_at);
        if (dateRange.from && isBefore(regDate, startOfDay(dateRange.from))) return false;
        if (dateRange.to && isAfter(regDate, dateRange.to)) return false;
        return true;
      });
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case "name":
          const nameA = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
          const nameB = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        case "email":
          comparison = (a.email || "").localeCompare(b.email || "");
          break;
        case "attendee_type":
          comparison = (a.attendee_type || "").localeCompare(b.attendee_type || "");
          break;
        case "created_at":
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case "ticket_count":
          comparison = (a.ticket_count || 1) - (b.ticket_count || 1);
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [registrations, searchQuery, sortField, sortDirection, attendeeTypeFilter, statusFilter, dateRange]);

  // Pagination
  const totalPages = Math.ceil(filteredRegistrations.length / pageSize);
  const paginatedRegistrations = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRegistrations.slice(start, start + pageSize);
  }, [filteredRegistrations, currentPage, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, attendeeTypeFilter, statusFilter, datePreset, customDateFrom, customDateTo]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" 
      ? <ChevronUp className="h-4 w-4" /> 
      : <ChevronDown className="h-4 w-4" />;
  };

  const getStatusBadge = (reg: Registration) => {
    if (reg.has_ticket_conflict && !reg.reviewed_at) {
      return (
        <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400 gap-1">
          <AlertTriangle className="h-3 w-3" />
          Review
        </Badge>
      );
    }
    if (reg.eventbrite_status || reg.registration_source === "eventbrite") {
      return (
        <Badge variant="outline" className="border-orange-500/50 text-orange-600 dark:text-orange-400 gap-1">
          <ExternalLink className="h-3 w-3" />
          Eventbrite
        </Badge>
      );
    }
    if (reg.reviewed_at) {
      return <Badge variant="outline" className="border-emerald-500/50 text-emerald-600 dark:text-emerald-400">Verified</Badge>;
    }
    return <Badge variant="secondary">Local</Badge>;
  };

  const clearFilters = () => {
    setSearchQuery("");
    setAttendeeTypeFilter("all");
    setStatusFilter("all");
    setDatePreset("all");
    setCustomDateFrom(undefined);
    setCustomDateTo(undefined);
  };

  const hasActiveFilters = searchQuery || attendeeTypeFilter !== "all" || statusFilter !== "all" || datePreset !== "all";

  const getDateLabel = () => {
    switch (datePreset) {
      case "today": return "Today";
      case "7days": return "Last 7 days";
      case "30days": return "Last 30 days";
      case "custom": 
        if (customDateFrom && customDateTo) {
          return `${format(customDateFrom, "MMM d")} - ${format(customDateTo, "MMM d")}`;
        }
        return "Custom range";
      default: return "All dates";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-4 w-4" />
          All Registrations
        </CardTitle>
        <CardDescription>
          {filteredRegistrations.length} of {registrations.length} registrations
          {hasActiveFilters && " (filtered)"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters Row */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={attendeeTypeFilter} onValueChange={setAttendeeTypeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Ticket Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ticket Types</SelectItem>
                {attendeeTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="flagged">Needs Review</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="eventbrite">Eventbrite</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Range Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn(
                  "w-full sm:w-[180px] justify-start text-left font-normal",
                  datePreset !== "all" && "border-primary"
                )}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {getDateLabel()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="start">
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {(["all", "today", "7days", "30days", "custom"] as DatePreset[]).map(preset => (
                      <Button
                        key={preset}
                        size="sm"
                        variant={datePreset === preset ? "default" : "outline"}
                        onClick={() => setDatePreset(preset)}
                      >
                        {preset === "all" ? "All" : preset === "today" ? "Today" : preset === "7days" ? "7 days" : preset === "30days" ? "30 days" : "Custom"}
                      </Button>
                    ))}
                  </div>
                  {datePreset === "custom" && (
                    <div className="flex flex-col gap-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">From</p>
                          <Calendar
                            mode="single"
                            selected={customDateFrom}
                            onSelect={setCustomDateFrom}
                            className="rounded-md border pointer-events-auto"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">To</p>
                          <Calendar
                            mode="single"
                            selected={customDateTo}
                            onSelect={setCustomDateTo}
                            className="rounded-md border pointer-events-auto"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      <SortIcon field="name" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 transition-colors hidden md:table-cell"
                    onClick={() => handleSort("email")}
                  >
                    <div className="flex items-center gap-1">
                      Email
                      <SortIcon field="email" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleSort("attendee_type")}
                  >
                    <div className="flex items-center gap-1">
                      Ticket Type
                      <SortIcon field="attendee_type" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 transition-colors text-center"
                    onClick={() => handleSort("ticket_count")}
                  >
                    <div className="flex items-center gap-1 justify-center">
                      Qty
                      <SortIcon field="ticket_count" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 transition-colors hidden sm:table-cell"
                    onClick={() => handleSort("created_at")}
                  >
                    <div className="flex items-center gap-1">
                      Registered
                      <SortIcon field="created_at" />
                    </div>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRegistrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {hasActiveFilters 
                        ? "No registrations match your filters"
                        : "No registrations yet"
                      }
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRegistrations.map(reg => (
                    <TableRow 
                      key={reg.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => onViewDetails(reg)}
                    >
                      <TableCell>
                        <div className="font-medium">
                          {reg.first_name} {reg.last_name}
                        </div>
                        <div className="text-xs text-muted-foreground md:hidden">
                          {reg.email}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {reg.email}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {reg.attendee_type || "Not specified"}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {reg.ticket_count > 1 ? (
                          <Badge variant="secondary">{reg.ticket_count}</Badge>
                        ) : (
                          <span className="text-muted-foreground">1</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">
                        {format(parseISO(reg.created_at), "MMM d, h:mm a")}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(reg)}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewDetails(reg);
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Pagination Controls */}
        {filteredRegistrations.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Rows per page:</span>
              <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(Number(v) as PageSize)}>
                <SelectTrigger className="w-[70px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filteredRegistrations.length)} of {filteredRegistrations.length}
              {hasActiveFilters && ` (filtered from ${registrations.length})`}
            </p>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                <ChevronLeft className="h-4 w-4 -ml-2" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-3 text-sm">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
                <ChevronRight className="h-4 w-4 -ml-2" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RegistrationsTable;
